import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Package, ArrowUpCircle, ArrowDownCircle, Search, AlertTriangle, RefreshCw, ArrowRightLeft, Warehouse, DollarSign, TrendingUp, X, ChevronRight, Layers, MoveRight, CheckCircle2, Grid, ChevronDown, ChevronUp, Filter, Check, Square, CheckSquare, Trash2, Edit, ArchiveX, BarChart3 } from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';
import InventoryStats from '../../components/InventoryStats';

const WAREHOUSES = [
  { id: 'china', name: 'China', color: 'bg-red-500/20 text-red-400 border-red-500/30', bgFull: 'bg-red-500/10 border-red-500/30', icon: '🇨🇳' },
  { id: 'contenedor', name: 'Contenedor', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', bgFull: 'bg-yellow-500/10 border-yellow-500/30', icon: '📦' },
  { id: 'stock', name: 'Stock', color: 'bg-green-500/20 text-green-400 border-green-500/30', bgFull: 'bg-green-500/10 border-green-500/30', icon: '✅' },
  { id: 'sin_stock', name: 'Sin Stock', color: 'bg-orange-500/20 text-orange-400 border-orange-500/30', bgFull: 'bg-orange-500/10 border-orange-500/30', icon: '⚠️' },
  { id: 'archivados', name: 'Archivados', color: 'bg-gray-500/20 text-gray-400 border-gray-500/30', bgFull: 'bg-gray-500/10 border-gray-500/30', icon: '📁' }
];

const InventoryPanel = ({ onInventoryChange }) => {
  const [activeSubTab, setActiveSubTab] = useState('summary');
  const [summary, setSummary] = useState(null);
  const [products, setProducts] = useState([]);
  const [movements, setMovements] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ category: '', brand: '', warehouse: '', stock_status: '', search: '' });
  
  // Categories inventory state
  const [categoryInventory, setCategoryInventory] = useState({ categories: [], totals: {} });
  const [categoryFilters, setCategoryFilters] = useState({ search: '', sort_by: 'value', sort_order: 'desc', stock_status: '', warehouse: '' });
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [categoryDetail, setCategoryDetail] = useState(null);
  const [categoryDetailLoading, setCategoryDetailLoading] = useState(false);
  
  // Dialog states
  const [showMovementDialog, setShowMovementDialog] = useState(false);
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [showDetailDrawer, setShowDetailDrawer] = useState(false);
  const [drawerWarehouse, setDrawerWarehouse] = useState(null);
  const [drawerProducts, setDrawerProducts] = useState([]);
  const [drawerLoading, setDrawerLoading] = useState(false);
  
  // Estado para edición rápida de cantidad en bodega
  const [quickEditInventory, setQuickEditInventory] = useState({ productId: null, warehouse: null, value: 0 });
  const [savingInventory, setSavingInventory] = useState(false);
  
  // Movement state
  const [movementType, setMovementType] = useState('entrada');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [movementData, setMovementData] = useState({ quantity: 1, warehouse: 'stock', notes: '' });
  
  // Transfer state - con modo
  const [transferMode, setTransferMode] = useState('product'); // 'product', 'category', 'all'
  const [transferData, setTransferData] = useState({ 
    product_id: '', 
    from_warehouse: 'china', 
    to_warehouse: 'contenedor', 
    quantity: 1, 
    notes: '',
    category: ''
  });
  const [transferPreview, setTransferPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [transferExecuting, setTransferExecuting] = useState(false);
  const [showConfirmBulk, setShowConfirmBulk] = useState(false);

  // Estado para selección masiva en drawer de bodega
  const [selectedDrawerProducts, setSelectedDrawerProducts] = useState(new Set());
  const [showDrawerTransferDialog, setShowDrawerTransferDialog] = useState(false);
  const [drawerTransferTarget, setDrawerTransferTarget] = useState('stock');
  const [transferQuantities, setTransferQuantities] = useState({}); // { productId: quantity }
  
  // Estado para filtro de categoría en drawer
  const [drawerCategoryFilter, setDrawerCategoryFilter] = useState('all');
  
  // Estado para editar producto desde drawer
  const [editingProduct, setEditingProduct] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', price: 0, stock: 0 });

  // Cargar categorías y marcas
  useEffect(() => {
    const fetchFiltersData = async () => {
      try {
        const [categoriesRes, brandsRes] = await Promise.all([
          api.get('/categories'),
          api.get('/brands')
        ]);
        setCategories(categoriesRes.data);
        setBrands(brandsRes.data);
      } catch (error) {
        console.error('Error fetching filters data:', error);
      }
    };
    fetchFiltersData();
  }, []);

  // Cargar productos al inicio
  useEffect(() => {
    const loadInitialProducts = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await api.get('/inventory/products', { headers: { Authorization: `Bearer ${token}` } });
        setProducts(res.data);
      } catch (error) {
        console.error('Error loading products:', error);
      }
    };
    loadInitialProducts();
  }, []);

  // Cargar datos según pestaña activa
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      if (activeSubTab === 'summary') {
        const res = await api.get('/inventory/summary', { headers });
        setSummary(res.data);
      } else if (activeSubTab === 'products') {
        const params = new URLSearchParams();
        if (filters.category) params.append('category', filters.category);
        if (filters.brand) params.append('brand', filters.brand);
        if (filters.warehouse) params.append('warehouse', filters.warehouse);
        if (filters.stock_status) params.append('stock_status', filters.stock_status);
        if (filters.search) params.append('search', filters.search);
        const res = await api.get(`/inventory/products?${params}`, { headers });
        setProducts(res.data);
      } else if (activeSubTab === 'categories') {
        const params = new URLSearchParams();
        if (categoryFilters.search) params.append('search', categoryFilters.search);
        if (categoryFilters.sort_by) params.append('sort_by', categoryFilters.sort_by);
        if (categoryFilters.sort_order) params.append('sort_order', categoryFilters.sort_order);
        if (categoryFilters.stock_status) params.append('stock_status', categoryFilters.stock_status);
        if (categoryFilters.warehouse) params.append('warehouse', categoryFilters.warehouse);
        const res = await api.get(`/inventory/by-category?${params}`, { headers });
        setCategoryInventory(res.data);
      } else if (activeSubTab === 'movements') {
        const res = await api.get('/inventory/movements?limit=100', { headers });
        setMovements(res.data);
      } else if (activeSubTab === 'transfers') {
        const res = await api.get('/inventory/transfers?limit=100', { headers });
        setTransfers(res.data);
      }
    } catch (error) {
      toast.error('Error cargando datos');
    } finally {
      setLoading(false);
    }
  }, [activeSubTab, filters, categoryFilters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Cargar detalle de bodega para drawer
  const loadWarehouseDetail = useCallback(async (warehouseId) => {
    setDrawerLoading(true);
    try {
      const token = localStorage.getItem('token');
      let url = '/inventory/products';
      if (warehouseId && warehouseId !== 'all') {
        url = `/inventory/by-warehouse/${warehouseId}`;
      }
      const res = await api.get(url, { headers: { Authorization: `Bearer ${token}` } });
      
      if (warehouseId === 'all') {
        setDrawerProducts(res.data);
      } else {
        setDrawerProducts(res.data.products || res.data);
      }
    } catch (error) {
      toast.error('Error cargando detalle');
    } finally {
      setDrawerLoading(false);
    }
  }, []);

  // Abrir drawer de detalle
  const openWarehouseDetail = useCallback((warehouseId) => {
    setDrawerWarehouse(warehouseId);
    setShowDetailDrawer(true);
    loadWarehouseDetail(warehouseId);
  }, [loadWarehouseDetail]);

  // Cerrar drawer
  const closeDrawer = useCallback(() => {
    setShowDetailDrawer(false);
    setDrawerWarehouse(null);
    setDrawerProducts([]);
    setQuickEditInventory({ productId: null, warehouse: null, value: 0 });
    setSelectedDrawerProducts(new Set());
    setShowDrawerTransferDialog(false);
    setDrawerCategoryFilter('all');
  }, []);

  // Funciones para selección masiva en drawer
  const toggleDrawerProductSelection = (productId) => {
    const newSelection = new Set(selectedDrawerProducts);
    if (newSelection.has(productId)) {
      newSelection.delete(productId);
    } else {
      newSelection.add(productId);
    }
    setSelectedDrawerProducts(newSelection);
  };

  const selectAllDrawerProducts = () => {
    const allIds = new Set(drawerProducts.map(p => p.id));
    setSelectedDrawerProducts(allIds);
  };

  const clearDrawerSelection = () => {
    setSelectedDrawerProducts(new Set());
  };

  // Transferencia masiva de productos seleccionados en drawer
  const handleDrawerBulkTransfer = async () => {
    if (selectedDrawerProducts.size === 0 || !drawerWarehouse || drawerWarehouse === 'all') return;
    
    setTransferExecuting(true);
    try {
      const token = localStorage.getItem('token');
      let transferred = 0;
      let totalUnits = 0;
      
      for (const productId of selectedDrawerProducts) {
        const product = drawerProducts.find(p => p.id === productId);
        if (!product) continue;
        
        // Usar la cantidad específica del input, o la cantidad completa si no se especificó
        const inv = product.inventory || { china: 0, contenedor: 0, stock: 0 };
        const maxQuantity = product.quantity || inv[drawerWarehouse] || 0;
        const quantity = transferQuantities[productId] !== undefined 
          ? Math.min(Math.max(1, transferQuantities[productId]), maxQuantity)
          : maxQuantity;
        
        if (quantity > 0) {
          try {
            await api.post('/inventory/transfer', {
              product_id: productId,
              from_warehouse: drawerWarehouse,
              to_warehouse: drawerTransferTarget,
              quantity: quantity
            }, { headers: { Authorization: `Bearer ${token}` } });
            transferred++;
            totalUnits += quantity;
          } catch (err) {
            console.error(`Error transferring product ${productId}:`, err);
          }
        }
      }
      
      toast.success(`${transferred} producto(s) transferidos (${totalUnits} unidades)`);
      setSelectedDrawerProducts(new Set());
      setTransferQuantities({});
      setShowDrawerTransferDialog(false);
      loadWarehouseDetail(drawerWarehouse);
      fetchData();
      if (onInventoryChange) onInventoryChange();
    } catch (error) {
      toast.error('Error al transferir productos');
    } finally {
      setTransferExecuting(false);
    }
  };

  // Eliminar productos seleccionados desde Archivados
  const handleBulkDeleteFromArchived = async () => {
    if (selectedDrawerProducts.size === 0 || drawerWarehouse !== 'archivados') return;
    
    const confirmDelete = window.confirm(
      `¿Estás seguro de eliminar ${selectedDrawerProducts.size} producto(s) permanentemente?\n\nEsta acción no se puede deshacer.`
    );
    
    if (!confirmDelete) return;
    
    setTransferExecuting(true);
    try {
      const token = localStorage.getItem('token');
      let deleted = 0;
      let errors = [];
      
      for (const productId of selectedDrawerProducts) {
        try {
          await api.delete(`/products/${productId}`, { headers: { Authorization: `Bearer ${token}` } });
          deleted++;
        } catch (err) {
          const product = drawerProducts.find(p => p.id === productId);
          errors.push(product?.name || productId);
        }
      }
      
      if (deleted > 0) {
        toast.success(`${deleted} producto(s) eliminado(s) permanentemente`);
      }
      if (errors.length > 0) {
        toast.error(`No se pudieron eliminar: ${errors.join(', ')}`);
      }
      
      setSelectedDrawerProducts(new Set());
      loadWarehouseDetail(drawerWarehouse);
      fetchData();
      if (onInventoryChange) onInventoryChange();
    } catch (error) {
      toast.error('Error al eliminar productos');
    } finally {
      setTransferExecuting(false);
    }
  };

  // Abrir diálogo para editar producto
  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setEditForm({
      name: product.name || '',
      price: product.price || 0,
      stock: product.inventory?.stock || product.stock || 0
    });
    setShowEditDialog(true);
  };

  // Guardar cambios de producto editado
  const handleSaveEditProduct = async () => {
    if (!editingProduct) return;
    try {
      const token = localStorage.getItem('token');
      await api.put(`/products/${editingProduct.id}`, {
        name: editForm.name,
        price: editForm.price,
        stock: editForm.stock,
        inventory: {
          ...editingProduct.inventory,
          stock: editForm.stock
        }
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success('Producto actualizado');
      setShowEditDialog(false);
      setEditingProduct(null);
      loadWarehouseDetail(drawerWarehouse);
      fetchData();
      if (onInventoryChange) onInventoryChange();
    } catch (error) {
      toast.error('Error al actualizar producto');
    }
  };

  // Archivar producto individual
  const handleArchiveProduct = async (productId) => {
    const confirmArchive = window.confirm('¿Deseas archivar este producto?\n\nEl producto será movido a la bodega "Archivados".');
    if (!confirmArchive) return;
    
    try {
      const token = localStorage.getItem('token');
      await api.post(`/products/${productId}/archive`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Producto archivado correctamente');
      loadWarehouseDetail(drawerWarehouse);
      fetchData();
      if (onInventoryChange) onInventoryChange();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al archivar producto');
    }
  };

  // Guardar cantidad de inventario rápidamente
  const handleQuickInventorySave = async (productId, warehouse) => {
    if (savingInventory) return;
    setSavingInventory(true);
    try {
      const token = localStorage.getItem('token');
      // Obtener producto actual para mantener los otros valores de inventory
      const productRes = await api.get(`/products/${productId}`);
      const currentProduct = productRes.data;
      const currentInventory = currentProduct.inventory || { china: 0, contenedor: 0, stock: 0 };
      
      // Actualizar solo la bodega específica
      const updatedInventory = {
        ...currentInventory,
        [warehouse]: quickEditInventory.value
      };
      
      // También actualizar stock legacy si es la bodega stock
      const updateData = { inventory: updatedInventory };
      if (warehouse === 'stock') {
        updateData.stock = quickEditInventory.value;
      }
      
      await api.put(`/products/${productId}`, updateData, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      
      toast.success(`Cantidad actualizada en ${warehouse}`);
      setQuickEditInventory({ productId: null, warehouse: null, value: 0 });
      
      // Recargar datos del drawer
      loadWarehouseDetail(drawerWarehouse);
      // Recargar resumen
      fetchData();
      // Notificar al Dashboard para que recargue "Productos Activos"
      if (onInventoryChange) onInventoryChange();
    } catch (error) {
      console.error('Error updating inventory:', error);
      toast.error('Error actualizando cantidad');
    } finally {
      setSavingInventory(false);
    }
  };

  // Cancelar edición rápida de inventario
  const cancelQuickInventoryEdit = () => {
    setQuickEditInventory({ productId: null, warehouse: null, value: 0 });
  };

  // Cargar detalle de categoría y expandir/colapsar
  const toggleCategoryDetail = useCallback(async (categorySlug) => {
    if (expandedCategory === categorySlug) {
      setExpandedCategory(null);
      setCategoryDetail(null);
      return;
    }
    
    setExpandedCategory(categorySlug);
    setCategoryDetailLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (categoryFilters.warehouse) params.append('warehouse', categoryFilters.warehouse);
      const res = await api.get(`/inventory/category/${categorySlug}?${params}`, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      setCategoryDetail(res.data);
    } catch (error) {
      toast.error('Error cargando detalle de categoría');
    } finally {
      setCategoryDetailLoading(false);
    }
  }, [expandedCategory, categoryFilters.warehouse]);

  // Iniciar transferencia por categoría desde la pestaña categorías
  const startCategoryTransfer = useCallback((categorySlug) => {
    setTransferMode('category');
    setTransferData({
      ...transferData,
      category: categorySlug,
      from_warehouse: 'china',
      to_warehouse: 'contenedor'
    });
    setShowTransferDialog(true);
  }, [transferData]);

  // Crear movimiento
  const handleCreateMovement = useCallback(async () => {
    if (!selectedProduct || movementData.quantity <= 0) {
      toast.error('Selecciona un producto y cantidad válida');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await api.post('/inventory/movements', {
        product_id: selectedProduct.id,
        movement_type: movementType,
        warehouse: movementData.warehouse,
        quantity: parseInt(movementData.quantity),
        notes: movementData.notes
      }, { headers: { Authorization: `Bearer ${token}` } });

      toast.success(`Movimiento registrado en ${WAREHOUSES.find(w => w.id === movementData.warehouse)?.name}`);
      setShowMovementDialog(false);
      setSelectedProduct(null);
      setMovementData({ quantity: 1, warehouse: 'stock', notes: '' });
      fetchData();
      // Recargar productos
      const res = await api.get('/inventory/products', { headers: { Authorization: `Bearer ${token}` } });
      setProducts(res.data);
      // Notificar al Dashboard para que recargue "Productos Activos"
      if (onInventoryChange) onInventoryChange();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar movimiento');
    }
  }, [selectedProduct, movementType, movementData, fetchData, onInventoryChange]);

  // Obtener preview de transferencia masiva
  const loadTransferPreview = useCallback(async () => {
    if (transferMode === 'product') return;
    
    if (transferMode === 'category' && !transferData.category) {
      setTransferPreview(null);
      return;
    }
    
    if (transferData.from_warehouse === transferData.to_warehouse) {
      setTransferPreview(null);
      return;
    }
    
    setPreviewLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await api.post('/inventory/transfer/preview', {
        transfer_type: transferMode,
        from_warehouse: transferData.from_warehouse,
        to_warehouse: transferData.to_warehouse,
        category: transferData.category || null,
        brand: null,
        notes: transferData.notes
      }, { headers: { Authorization: `Bearer ${token}` } });
      setTransferPreview(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cargar vista previa');
      setTransferPreview(null);
    } finally {
      setPreviewLoading(false);
    }
  }, [transferMode, transferData]);

  // Cargar preview cuando cambia el modo o los datos
  useEffect(() => {
    if (showTransferDialog && transferMode !== 'product') {
      const timer = setTimeout(() => {
        loadTransferPreview();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [transferMode, transferData.from_warehouse, transferData.to_warehouse, transferData.category, showTransferDialog, loadTransferPreview]);

  // Crear transferencia individual
  const handleCreateTransfer = useCallback(async () => {
    if (!transferData.product_id || transferData.quantity <= 0) {
      toast.error('Selecciona un producto y cantidad válida');
      return;
    }

    if (transferData.from_warehouse === transferData.to_warehouse) {
      toast.error('Las bodegas origen y destino deben ser diferentes');
      return;
    }

    setTransferExecuting(true);
    try {
      const token = localStorage.getItem('token');
      await api.post('/inventory/transfer', {
        product_id: transferData.product_id,
        from_warehouse: transferData.from_warehouse,
        to_warehouse: transferData.to_warehouse,
        quantity: parseInt(transferData.quantity),
        notes: transferData.notes
      }, { headers: { Authorization: `Bearer ${token}` } });

      const fromName = WAREHOUSES.find(w => w.id === transferData.from_warehouse)?.name;
      const toName = WAREHOUSES.find(w => w.id === transferData.to_warehouse)?.name;
      toast.success(`Transferencia completada: ${fromName} → ${toName}`);
      
      closeTransferDialog();
      fetchData();
      // Recargar productos
      const res = await api.get('/inventory/products', { headers: { Authorization: `Bearer ${token}` } });
      setProducts(res.data);
      // Notificar al Dashboard para que recargue "Productos Activos"
      if (onInventoryChange) onInventoryChange();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear transferencia');
    } finally {
      setTransferExecuting(false);
    }
  }, [transferData, fetchData, onInventoryChange]);

  // Crear transferencia masiva
  const handleBulkTransfer = useCallback(async () => {
    if (transferData.from_warehouse === transferData.to_warehouse) {
      toast.error('Las bodegas origen y destino deben ser diferentes');
      return;
    }

    if (transferMode === 'category' && !transferData.category) {
      toast.error('Selecciona una categoría');
      return;
    }

    setTransferExecuting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await api.post('/inventory/transfer/bulk', {
        transfer_type: transferMode,
        from_warehouse: transferData.from_warehouse,
        to_warehouse: transferData.to_warehouse,
        category: transferData.category || null,
        brand: null,
        notes: transferData.notes
      }, { headers: { Authorization: `Bearer ${token}` } });

      toast.success(`${res.data.total_products} productos transferidos (${res.data.total_units} unidades)`);
      
      closeTransferDialog();
      setShowConfirmBulk(false);
      fetchData();
      // Recargar productos
      const prodRes = await api.get('/inventory/products', { headers: { Authorization: `Bearer ${token}` } });
      setProducts(prodRes.data);
      // Notificar al Dashboard para que recargue "Productos Activos"
      if (onInventoryChange) onInventoryChange();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear transferencia masiva');
    } finally {
      setTransferExecuting(false);
    }
  }, [transferMode, transferData, fetchData, onInventoryChange]);

  // Cerrar dialog de transferencia y limpiar estado
  const closeTransferDialog = useCallback(() => {
    setShowTransferDialog(false);
    setTransferMode('product');
    setTransferData({ 
      product_id: '', 
      from_warehouse: 'china', 
      to_warehouse: 'contenedor', 
      quantity: 1, 
      notes: '',
      category: ''
    });
    setTransferPreview(null);
    setShowConfirmBulk(false);
  }, []);

  // Cerrar dialog de movimiento y limpiar estado
  const closeMovementDialog = useCallback(() => {
    setShowMovementDialog(false);
    setSelectedProduct(null);
    setMovementData({ quantity: 1, warehouse: 'stock', notes: '' });
  }, []);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(value || 0);
  };

  // ============================================
  // RENDER: RESUMEN POR BODEGAS (con tarjetas clickeables)
  // ============================================
  const renderSummary = () => {
    if (!summary) return <p className="text-white/40">Cargando...</p>;

    const { warehouses, grand_total_units, grand_total_value } = summary;

    return (
      <div className="space-y-6 md:space-y-8">
        {/* Tarjetas de Bodegas Principales - CLICKEABLES */}
        <div>
          <h3 className="text-xs md:text-sm uppercase tracking-wider text-white/60 mb-3">Bodegas Activas</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {WAREHOUSES.filter(wh => ['china', 'contenedor', 'stock'].includes(wh.id)).map((wh) => {
              const data = warehouses?.[wh.id] || { total_units: 0, total_value: 0, total_products: 0 };
              return (
                <button
                  key={wh.id}
                  onClick={() => openWarehouseDetail(wh.id)}
                  className="bg-gradient-to-br from-[#111] to-[#0A0A0A] border border-white/10 p-4 md:p-6 text-left hover:border-white/30 hover:from-[#151515] transition-all duration-300 cursor-pointer group"
                  data-testid={`warehouse-card-${wh.id}`}
                >
                  <div className="flex items-center justify-between mb-3 md:mb-4">
                    <div className="flex items-center gap-2 md:gap-3">
                      <Warehouse className="w-4 h-4 md:w-5 md:h-5 text-white/60" />
                      <h3 className="text-base md:text-lg font-semibold">{wh.name}</h3>
                    </div>
                    <div className="flex items-center gap-1 md:gap-2">
                      <span className={`px-2 md:px-3 py-0.5 md:py-1 text-[10px] md:text-xs uppercase tracking-wider border ${wh.color}`}>
                        {data.total_products} prod
                      </span>
                      <ChevronRight className="w-4 h-4 text-white/40 group-hover:text-white/80 transition-colors hidden sm:block" />
                    </div>
                  </div>
                  
                  <div className="space-y-3 md:space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-xs md:text-sm text-white/60">Unidades</span>
                      <span className="text-xl md:text-2xl font-bold">{data.total_units.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center border-t border-white/10 pt-3 md:pt-4">
                      <span className="text-xs md:text-sm text-white/60 flex items-center gap-1">
                        <DollarSign className="w-3 h-3 md:w-4 md:h-4" />
                        <span className="hidden sm:inline">Valor Invertido</span>
                        <span className="sm:hidden">Valor</span>
                      </span>
                      <span className="text-base md:text-xl font-semibold text-green-400">{formatCurrency(data.total_value)}</span>
                    </div>
                  </div>
                  
                  <div className="mt-4 pt-3 border-t border-white/5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <span className="text-xs text-white/50">Click para ver detalle</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Tarjetas de Bodegas Secundarias (Sin Stock y Archivados) */}
        <div>
          <h3 className="text-xs md:text-sm uppercase tracking-wider text-white/60 mb-3">Productos Inactivos</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
            {WAREHOUSES.filter(wh => ['sin_stock', 'archivados'].includes(wh.id)).map((wh) => {
              const data = warehouses?.[wh.id] || { total_units: 0, total_value: 0, total_products: 0 };
              return (
                <button
                  key={wh.id}
                  onClick={() => openWarehouseDetail(wh.id)}
                  className={`bg-gradient-to-br from-[#111] to-[#0A0A0A] border p-4 md:p-5 text-left hover:border-white/30 hover:from-[#151515] transition-all duration-300 cursor-pointer group ${wh.bgFull}`}
                  data-testid={`warehouse-card-${wh.id}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2 md:gap-3">
                      {wh.id === 'sin_stock' ? (
                        <AlertTriangle className="w-4 h-4 md:w-5 md:h-5 text-orange-400" />
                      ) : (
                        <Layers className="w-4 h-4 md:w-5 md:h-5 text-gray-400" />
                      )}
                      <h3 className="text-sm md:text-base font-semibold">{wh.name}</h3>
                    </div>
                    <div className="flex items-center gap-1 md:gap-2">
                      <span className={`px-2 py-0.5 text-[10px] md:text-xs uppercase tracking-wider border ${wh.color}`}>
                        {data.total_products} prod
                      </span>
                      <ChevronRight className="w-4 h-4 text-white/40 group-hover:text-white/80 transition-colors hidden sm:block" />
                    </div>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-xs text-white/40">Unidades</p>
                      <p className="text-lg md:text-xl font-bold">{data.total_units.toLocaleString()}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-white/40">Valor</p>
                      <p className="text-sm md:text-base font-semibold text-green-400">{formatCurrency(data.total_value)}</p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Total General - CLICKEABLE */}
        <button
          onClick={() => openWarehouseDetail('all')}
          className="w-full bg-gradient-to-r from-[#1a1a1a] to-[#0D0D0D] border-2 border-white/20 p-4 md:p-6 text-left hover:border-white/40 transition-all duration-300 cursor-pointer group"
          data-testid="warehouse-card-total"
        >
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-2 md:gap-3">
              <TrendingUp className="w-5 h-5 md:w-6 md:h-6 text-white/60" />
              <div>
                <h3 className="text-xs md:text-sm uppercase tracking-wider text-white/60">Total General</h3>
                <p className="text-[10px] md:text-xs text-white/40 hidden sm:block">Todas las bodegas • Click para detalle</p>
              </div>
            </div>
            <div className="flex justify-between items-center">
              <div className="text-left">
                <p className="text-[10px] md:text-sm text-white/60">Unidades</p>
                <p className="text-2xl md:text-3xl font-bold">{grand_total_units?.toLocaleString() || 0}</p>
              </div>
              <div className="text-right">
                <p className="text-[10px] md:text-sm text-white/60">Valor Total</p>
                <p className="text-xl md:text-3xl font-bold text-green-400">{formatCurrency(grand_total_value)}</p>
              </div>
              <ChevronRight className="w-5 h-5 text-white/40 group-hover:text-white/80 transition-colors hidden sm:block" />
            </div>
          </div>
        </button>

        {/* Estadísticas de Stock - CLICKEABLES */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
          <button
            onClick={() => {
              setFilters({ ...filters, stock_status: '' });
              setActiveSubTab('products');
            }}
            className="bg-[#0A0A0A] border border-white/10 p-3 md:p-4 text-left hover:border-white/30 transition-all cursor-pointer"
          >
            <p className="text-[10px] md:text-xs uppercase tracking-wider text-white/40 mb-1 md:mb-2">Total Productos</p>
            <p className="text-xl md:text-2xl font-semibold">{summary.total_products}</p>
          </button>
          <button
            onClick={() => {
              setFilters({ ...filters, stock_status: 'in_stock' });
              setActiveSubTab('products');
            }}
            className="bg-[#0A0A0A] border border-white/10 p-3 md:p-4 text-left hover:border-green-500/30 transition-all cursor-pointer"
          >
            <p className="text-[10px] md:text-xs uppercase tracking-wider text-white/40 mb-1 md:mb-2">Con Stock</p>
            <p className="text-xl md:text-2xl font-semibold text-green-400">{summary.with_stock}</p>
          </button>
          <button
            onClick={() => {
              setFilters({ ...filters, stock_status: 'low' });
              setActiveSubTab('products');
            }}
            className="bg-[#0A0A0A] border border-white/10 p-3 md:p-4 text-left hover:border-yellow-500/30 transition-all cursor-pointer"
          >
            <p className="text-[10px] md:text-xs uppercase tracking-wider text-white/40 mb-1 md:mb-2">Stock Bajo</p>
            <p className="text-xl md:text-2xl font-semibold text-yellow-400">{summary.low_stock}</p>
          </button>
          <button
            onClick={() => {
              setFilters({ ...filters, stock_status: 'out' });
              setActiveSubTab('products');
            }}
            className="bg-[#0A0A0A] border border-white/10 p-3 md:p-4 text-left hover:border-red-500/30 transition-all cursor-pointer"
          >
            <p className="text-[10px] md:text-xs uppercase tracking-wider text-white/40 mb-1 md:mb-2">Sin Stock</p>
            <p className="text-xl md:text-2xl font-semibold text-red-400">{summary.out_of_stock}</p>
          </button>
        </div>

        {/* Flujo de Mercancía Principal */}
        <div className="bg-[#0A0A0A] border border-white/10 p-4 md:p-6">
          <h3 className="text-xs md:text-sm uppercase tracking-wider text-white/60 mb-3 md:mb-4">Flujo de Mercancía</h3>
          
          {/* Flujo principal - desktop */}
          <div className="hidden md:flex items-center justify-center gap-3 text-center mb-4">
            <button
              onClick={() => openWarehouseDetail('china')}
              className="flex-1 p-4 border border-red-500/30 bg-red-500/10 hover:bg-red-500/20 transition-all cursor-pointer"
            >
              <p className="text-xs text-white/60 mb-1">CHINA</p>
              <p className="text-xl font-bold">{warehouses?.china?.total_units || 0}</p>
            </button>
            <MoveRight className="w-6 h-6 text-white/40 flex-shrink-0" />
            <button
              onClick={() => openWarehouseDetail('contenedor')}
              className="flex-1 p-4 border border-yellow-500/30 bg-yellow-500/10 hover:bg-yellow-500/20 transition-all cursor-pointer"
            >
              <p className="text-xs text-white/60 mb-1">CONTENEDOR</p>
              <p className="text-xl font-bold">{warehouses?.contenedor?.total_units || 0}</p>
            </button>
            <MoveRight className="w-6 h-6 text-white/40 flex-shrink-0" />
            <button
              onClick={() => openWarehouseDetail('stock')}
              className="flex-1 p-4 border border-green-500/30 bg-green-500/10 hover:bg-green-500/20 transition-all cursor-pointer"
            >
              <p className="text-xs text-white/60 mb-1">STOCK</p>
              <p className="text-xl font-bold">{warehouses?.stock?.total_units || 0}</p>
            </button>
            <MoveRight className="w-6 h-6 text-white/40 flex-shrink-0" />
            <div className="flex flex-col gap-2">
              <button
                onClick={() => openWarehouseDetail('sin_stock')}
                className="p-3 border border-orange-500/30 bg-orange-500/10 hover:bg-orange-500/20 transition-all cursor-pointer"
              >
                <p className="text-[10px] text-white/60">SIN STOCK</p>
                <p className="text-lg font-bold">{warehouses?.sin_stock?.total_units || 0}</p>
              </button>
              <button
                onClick={() => openWarehouseDetail('archivados')}
                className="p-3 border border-gray-500/30 bg-gray-500/10 hover:bg-gray-500/20 transition-all cursor-pointer"
              >
                <p className="text-[10px] text-white/60">ARCHIVADOS</p>
                <p className="text-lg font-bold">{warehouses?.archivados?.total_units || 0}</p>
              </button>
            </div>
          </div>
          
          {/* Flujo principal - móvil */}
          <div className="md:hidden grid grid-cols-3 gap-2 mb-3">
            <button
              onClick={() => openWarehouseDetail('china')}
              className="p-2 border border-red-500/30 bg-red-500/10 hover:bg-red-500/20 transition-all cursor-pointer text-center"
            >
              <p className="text-[10px] text-white/60">CHINA</p>
              <p className="text-lg font-bold">{warehouses?.china?.total_units || 0}</p>
            </button>
            <button
              onClick={() => openWarehouseDetail('contenedor')}
              className="p-2 border border-yellow-500/30 bg-yellow-500/10 hover:bg-yellow-500/20 transition-all cursor-pointer text-center"
            >
              <p className="text-[10px] text-white/60">CONT.</p>
              <p className="text-lg font-bold">{warehouses?.contenedor?.total_units || 0}</p>
            </button>
            <button
              onClick={() => openWarehouseDetail('stock')}
              className="p-2 border border-green-500/30 bg-green-500/10 hover:bg-green-500/20 transition-all cursor-pointer text-center"
            >
              <p className="text-[10px] text-white/60">STOCK</p>
              <p className="text-lg font-bold">{warehouses?.stock?.total_units || 0}</p>
            </button>
          </div>
          <div className="md:hidden grid grid-cols-2 gap-2">
            <button
              onClick={() => openWarehouseDetail('sin_stock')}
              className="p-2 border border-orange-500/30 bg-orange-500/10 hover:bg-orange-500/20 transition-all cursor-pointer text-center"
            >
              <p className="text-[10px] text-white/60">SIN STOCK</p>
              <p className="text-lg font-bold">{warehouses?.sin_stock?.total_units || 0}</p>
            </button>
            <button
              onClick={() => openWarehouseDetail('archivados')}
              className="p-2 border border-gray-500/30 bg-gray-500/10 hover:bg-gray-500/20 transition-all cursor-pointer text-center"
            >
              <p className="text-[10px] text-white/60">ARCHIVADOS</p>
              <p className="text-lg font-bold">{warehouses?.archivados?.total_units || 0}</p>
            </button>
          </div>
          
          <p className="text-center text-[10px] md:text-xs text-white/40 mt-3 md:mt-4">
            Solo productos con Stock &gt; 0 se muestran públicamente
          </p>
        </div>

        {/* Últimas Transferencias */}
        {summary.recent_transfers && summary.recent_transfers.length > 0 && (
          <div className="bg-[#0A0A0A] border border-white/10 p-4 md:p-6">
            <h3 className="text-xs md:text-sm uppercase tracking-wider text-white/60 mb-3 md:mb-4">Últimas Transferencias</h3>
            <div className="space-y-2">
              {summary.recent_transfers.slice(0, 5).map((t, idx) => (
                <div key={idx} className="flex items-center justify-between py-2 border-b border-white/5">
                  <div className="min-w-0 flex-1 mr-2">
                    <p className="text-xs md:text-sm truncate">{t.product_name}</p>
                    <p className="text-[10px] md:text-xs text-white/40">
                      {WAREHOUSES.find(w => w.id === t.from_warehouse)?.name} → {WAREHOUSES.find(w => w.id === t.to_warehouse)?.name}
                    </p>
                  </div>
                  <span className="text-xs md:text-sm font-semibold flex-shrink-0">{t.quantity} uds</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // ============================================
  // RENDER: PRODUCTOS CON INVENTARIO POR BODEGA
  // ============================================
  const renderProducts = () => (
    <div>
      {/* Filtros - Responsive */}
      <div className="flex flex-wrap gap-2 md:gap-4 mb-4 md:mb-6">
        <div className="w-full sm:flex-1 sm:min-w-[180px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
            <input
              type="text"
              placeholder="Buscar producto..."
              value={filters.search}
              onChange={(e) => setFilters({...filters, search: e.target.value})}
              className="w-full bg-[#0A0A0A] border border-white/10 pl-10 pr-4 py-2 text-sm outline-none focus:border-white/30"
            />
          </div>
        </div>
        <select
          value={filters.warehouse}
          onChange={(e) => setFilters({...filters, warehouse: e.target.value})}
          className="flex-1 sm:flex-none bg-[#0A0A0A] border border-white/10 px-3 md:px-4 py-2 text-xs md:text-sm outline-none cursor-pointer"
        >
          <option value="">Bodegas</option>
          {WAREHOUSES.map(wh => (
            <option key={wh.id} value={wh.id}>{wh.name}</option>
          ))}
        </select>
        <select
          value={filters.category}
          onChange={(e) => setFilters({...filters, category: e.target.value})}
          className="flex-1 sm:flex-none bg-[#0A0A0A] border border-white/10 px-3 md:px-4 py-2 text-xs md:text-sm outline-none cursor-pointer"
        >
          <option value="">Categorías</option>
          {categories.map((cat) => (
            <option key={cat.id} value={cat.slug}>{cat.name}</option>
          ))}
        </select>
        <select
          value={filters.brand}
          onChange={(e) => setFilters({...filters, brand: e.target.value})}
          className="flex-1 sm:flex-none bg-[#0A0A0A] border border-white/10 px-3 md:px-4 py-2 text-xs md:text-sm outline-none cursor-pointer"
        >
          <option value="">Marcas</option>
          {brands.map((brand) => (
            <option key={brand.id} value={brand.name}>{brand.name}</option>
          ))}
        </select>
        <select
          value={filters.stock_status}
          onChange={(e) => setFilters({...filters, stock_status: e.target.value})}
          className="flex-1 sm:flex-none bg-[#0A0A0A] border border-white/10 px-3 md:px-4 py-2 text-xs md:text-sm outline-none cursor-pointer"
        >
          <option value="">Estado</option>
          <option value="in_stock">Con stock</option>
          <option value="low">Stock bajo</option>
          <option value="out">Sin stock</option>
        </select>
        {(filters.category || filters.brand || filters.warehouse || filters.stock_status || filters.search) && (
          <button 
            onClick={() => setFilters({ category: '', brand: '', warehouse: '', stock_status: '', search: '' })}
            className="px-3 md:px-4 py-2 text-[10px] md:text-xs uppercase tracking-wider text-white/60 hover:text-white border border-white/10 hover:border-white/30 transition-all"
          >
            Limpiar
          </button>
        )}
      </div>

      {/* Botón de transferencia masiva si hay filtros activos */}
      {(filters.category || filters.warehouse) && products.length > 0 && (
        <div className="mb-4 p-3 md:p-4 bg-blue-500/10 border border-blue-500/30 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <p className="text-xs md:text-sm font-semibold text-blue-400">Transferencia rápida</p>
            <p className="text-[10px] md:text-xs text-white/60">
              Transferir los {products.length} productos filtrados
            </p>
          </div>
          <button
            onClick={() => {
              setTransferMode(filters.category ? 'category' : 'all');
              setTransferData({
                ...transferData,
                category: filters.category,
                from_warehouse: filters.warehouse || 'china'
              });
              setShowTransferDialog(true);
            }}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-[10px] md:text-xs uppercase tracking-wider transition-colors w-full sm:w-auto text-center"
          >
            Transferir
          </button>
        </div>
      )}

      {/* Tabla de productos - Mobile optimized */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-white/10 text-left">
              <th className="pb-3 text-xs uppercase tracking-wider text-white/60">Producto</th>
              <th className="pb-3 text-xs uppercase tracking-wider text-white/60 text-center">China</th>
              <th className="pb-3 text-xs uppercase tracking-wider text-white/60 text-center">Cont.</th>
              <th className="pb-3 text-xs uppercase tracking-wider text-white/60 text-center">Stock</th>
              <th className="pb-3 text-xs uppercase tracking-wider text-white/60 text-center">Total</th>
              <th className="pb-3 text-xs uppercase tracking-wider text-white/60 text-right">Valor</th>
              <th className="pb-3 text-xs uppercase tracking-wider text-white/60 text-center">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {products.map((product) => {
              const inv = product.inventory || { china: 0, contenedor: 0, stock: 0 };
              const total = product.total_inventory || (inv.china + inv.contenedor + inv.stock);
              const costPrice = product.cost_price || product.price;
              const totalValue = total * costPrice;
              const stockQty = inv.stock || 0;
              
              return (
                <tr key={product.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                  <td className="py-3">
                    <div>
                      <p className="font-medium text-sm">{product.name}</p>
                      <p className="text-xs text-white/40">{product.brand} • {product.sku}</p>
                    </div>
                  </td>
                  <td className="py-3 text-center">
                    <span className={`px-2 py-0.5 text-xs ${inv.china > 0 ? 'bg-red-500/20 text-red-400' : 'text-white/30'}`}>
                      {inv.china || 0}
                    </span>
                  </td>
                  <td className="py-3 text-center">
                    <span className={`px-2 py-0.5 text-xs ${inv.contenedor > 0 ? 'bg-yellow-500/20 text-yellow-400' : 'text-white/30'}`}>
                      {inv.contenedor || 0}
                    </span>
                  </td>
                  <td className="py-3 text-center">
                    <span className={`px-2 py-0.5 text-xs font-semibold ${
                      stockQty > 5 ? 'bg-green-500/20 text-green-400' :
                      stockQty > 0 ? 'bg-yellow-500/20 text-yellow-400' :
                      'bg-red-500/20 text-red-400'
                    }`}>
                      {stockQty}
                    </span>
                  </td>
                  <td className="py-3 text-center font-semibold text-sm">{total}</td>
                  <td className="py-3 text-right text-green-400 text-sm">{formatCurrency(totalValue)}</td>
                  <td className="py-3">
                    <div className="flex justify-center gap-1">
                      <button
                        onClick={() => {
                          setSelectedProduct(product);
                          setMovementType('entrada');
                          setShowMovementDialog(true);
                        }}
                        className="p-1.5 hover:bg-white/10 text-green-400 transition-colors"
                        title="Entrada"
                      >
                        <ArrowDownCircle className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => {
                          setSelectedProduct(product);
                          setMovementType('salida');
                          setShowMovementDialog(true);
                        }}
                        className="p-1.5 hover:bg-white/10 text-red-400 transition-colors"
                        title="Salida"
                      >
                        <ArrowUpCircle className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => {
                          setTransferMode('product');
                          setTransferData({ ...transferData, product_id: product.id });
                          setShowTransferDialog(true);
                        }}
                        className="p-1.5 hover:bg-white/10 text-blue-400 transition-colors"
                        title="Transferir"
                      >
                        <ArrowRightLeft className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards View */}
      <div className="md:hidden space-y-3">
        {products.map((product) => {
          const inv = product.inventory || { china: 0, contenedor: 0, stock: 0 };
          const total = product.total_inventory || (inv.china + inv.contenedor + inv.stock);
          const costPrice = product.cost_price || product.price;
          const totalValue = total * costPrice;
          const stockQty = inv.stock || 0;
          
          return (
            <div key={product.id} className="bg-[#0A0A0A] border border-white/10 p-3">
              <div className="flex justify-between items-start mb-3">
                <div className="flex-1 min-w-0 mr-2">
                  <p className="font-medium text-sm truncate">{product.name}</p>
                  <p className="text-[10px] text-white/40">{product.brand} • {product.sku}</p>
                </div>
                <p className="text-green-400 text-sm font-semibold">{formatCurrency(totalValue)}</p>
              </div>
              
              {/* Stock por bodega */}
              <div className="grid grid-cols-4 gap-2 mb-3 text-center">
                <div>
                  <p className="text-[10px] text-white/40">China</p>
                  <span className={`text-xs ${inv.china > 0 ? 'text-red-400' : 'text-white/30'}`}>{inv.china || 0}</span>
                </div>
                <div>
                  <p className="text-[10px] text-white/40">Cont.</p>
                  <span className={`text-xs ${inv.contenedor > 0 ? 'text-yellow-400' : 'text-white/30'}`}>{inv.contenedor || 0}</span>
                </div>
                <div>
                  <p className="text-[10px] text-white/40">Stock</p>
                  <span className={`text-xs font-semibold ${stockQty > 5 ? 'text-green-400' : stockQty > 0 ? 'text-yellow-400' : 'text-red-400'}`}>{stockQty}</span>
                </div>
                <div>
                  <p className="text-[10px] text-white/40">Total</p>
                  <span className="text-xs font-bold">{total}</span>
                </div>
              </div>
              
              {/* Acciones */}
              <div className="flex gap-2 border-t border-white/5 pt-3">
                <button
                  onClick={() => {
                    setSelectedProduct(product);
                    setMovementType('entrada');
                    setShowMovementDialog(true);
                  }}
                  className="flex-1 py-2 bg-green-500/20 text-green-400 text-[10px] uppercase tracking-wider hover:bg-green-500/30 transition-colors flex items-center justify-center gap-1"
                >
                  <ArrowDownCircle className="w-3 h-3" /> Entrada
                </button>
                <button
                  onClick={() => {
                    setSelectedProduct(product);
                    setMovementType('salida');
                    setShowMovementDialog(true);
                  }}
                  className="flex-1 py-2 bg-red-500/20 text-red-400 text-[10px] uppercase tracking-wider hover:bg-red-500/30 transition-colors flex items-center justify-center gap-1"
                >
                  <ArrowUpCircle className="w-3 h-3" /> Salida
                </button>
                <button
                  onClick={() => {
                    setTransferMode('product');
                    setTransferData({ ...transferData, product_id: product.id });
                    setShowTransferDialog(true);
                  }}
                  className="flex-1 py-2 bg-blue-500/20 text-blue-400 text-[10px] uppercase tracking-wider hover:bg-blue-500/30 transition-colors flex items-center justify-center gap-1"
                >
                  <ArrowRightLeft className="w-3 h-3" /> Mover
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  // ============================================
  // RENDER: HISTORIAL DE MOVIMIENTOS
  // ============================================
  const renderMovements = () => (
    <div className="space-y-4">
      {movements.length === 0 ? (
        <p className="text-white/40 text-center py-8">No hay movimientos registrados</p>
      ) : (
        movements.map((mov, idx) => (
          <div key={`mov-${mov.id || idx}`} className="flex items-center justify-between py-3 border-b border-white/5">
            <div className="flex items-center gap-4">
              {mov.movement_type.includes('entrada') || mov.movement_type === 'devolucion' ? (
                <ArrowDownCircle className="w-5 h-5 text-green-400" />
              ) : mov.movement_type.includes('transferencia') ? (
                <ArrowRightLeft className="w-5 h-5 text-blue-400" />
              ) : (
                <ArrowUpCircle className="w-5 h-5 text-red-400" />
              )}
              <div>
                <p className="font-medium">{mov.product_name}</p>
                <p className="text-xs text-white/40">
                  {mov.movement_type.toUpperCase()} 
                  {mov.warehouse && ` • ${WAREHOUSES.find(w => w.id === mov.warehouse)?.name || mov.warehouse}`}
                  {mov.notes && ` • ${mov.notes}`}
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className={`font-semibold ${mov.movement_type.includes('entrada') || mov.movement_type === 'devolucion' ? 'text-green-400' : mov.movement_type.includes('salida') || mov.movement_type === 'venta' ? 'text-red-400' : 'text-blue-400'}`}>
                {mov.movement_type.includes('entrada') || mov.movement_type === 'devolucion' ? '+' : '-'}{mov.quantity}
              </p>
              <p className="text-xs text-white/40">{mov.previous_stock} → {mov.new_stock}</p>
            </div>
          </div>
        ))
      )}
    </div>
  );

  // ============================================
  // RENDER: HISTORIAL DE TRANSFERENCIAS
  // ============================================
  const renderTransfers = () => (
    <div className="space-y-4">
      {transfers.length === 0 ? (
        <p className="text-white/40 text-center py-8">No hay transferencias registradas</p>
      ) : (
        transfers.map((t, idx) => (
          <div key={`trans-${t.id || idx}`} className="flex items-center justify-between py-3 border-b border-white/5 bg-[#0A0A0A] p-4">
            <div className="flex items-center gap-4">
              <ArrowRightLeft className="w-5 h-5 text-blue-400" />
              <div>
                <p className="font-medium">{t.product_name}</p>
                <p className="text-xs text-white/40">
                  {new Date(t.created_at).toLocaleDateString('es-CO')} {new Date(t.created_at).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' })}
                </p>
                {t.notes && <p className="text-xs text-white/50 mt-1">{t.notes}</p>}
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className={`px-3 py-1 text-xs uppercase ${WAREHOUSES.find(w => w.id === t.from_warehouse)?.color}`}>
                {WAREHOUSES.find(w => w.id === t.from_warehouse)?.name}
              </span>
              <ArrowRightLeft className="w-4 h-4 text-white/40" />
              <span className={`px-3 py-1 text-xs uppercase ${WAREHOUSES.find(w => w.id === t.to_warehouse)?.color}`}>
                {WAREHOUSES.find(w => w.id === t.to_warehouse)?.name}
              </span>
              <span className="font-semibold ml-4">{t.quantity} uds</span>
            </div>
          </div>
        ))
      )}
    </div>
  );

  // ============================================
  // RENDER: CATEGORÍAS CON INVENTARIO
  // ============================================
  const renderCategories = () => {
    const { categories: catList, totals } = categoryInventory;

    return (
      <div className="space-y-6">
        {/* Filtros de categorías */}
        <div className="flex flex-wrap gap-2 md:gap-4 mb-4">
          <div className="flex-1 min-w-[180px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
              <input
                type="text"
                placeholder="Buscar categoría..."
                value={categoryFilters.search}
                onChange={(e) => setCategoryFilters({...categoryFilters, search: e.target.value})}
                className="w-full bg-[#0A0A0A] border border-white/10 pl-10 pr-4 py-2 text-sm outline-none focus:border-white/30"
              />
            </div>
          </div>
          <select
            value={categoryFilters.warehouse}
            onChange={(e) => setCategoryFilters({...categoryFilters, warehouse: e.target.value})}
            className="bg-[#0A0A0A] border border-white/10 px-3 md:px-4 py-2 text-xs md:text-sm outline-none cursor-pointer"
          >
            <option value="">Todas las bodegas</option>
            {WAREHOUSES.map(wh => (
              <option key={wh.id} value={wh.id}>{wh.name}</option>
            ))}
          </select>
          <select
            value={categoryFilters.stock_status}
            onChange={(e) => setCategoryFilters({...categoryFilters, stock_status: e.target.value})}
            className="bg-[#0A0A0A] border border-white/10 px-3 md:px-4 py-2 text-xs md:text-sm outline-none cursor-pointer"
          >
            <option value="">Todos los estados</option>
            <option value="with_stock">Con stock</option>
            <option value="without_stock">Sin stock</option>
          </select>
          <select
            value={`${categoryFilters.sort_by}-${categoryFilters.sort_order}`}
            onChange={(e) => {
              const [sort_by, sort_order] = e.target.value.split('-');
              setCategoryFilters({...categoryFilters, sort_by, sort_order});
            }}
            className="bg-[#0A0A0A] border border-white/10 px-3 md:px-4 py-2 text-xs md:text-sm outline-none cursor-pointer"
          >
            <option value="value-desc">Mayor valor</option>
            <option value="value-asc">Menor valor</option>
            <option value="units-desc">Más unidades</option>
            <option value="units-asc">Menos unidades</option>
            <option value="products-desc">Más productos</option>
            <option value="name-asc">A-Z</option>
            <option value="name-desc">Z-A</option>
          </select>
        </div>

        {/* Totales por bodega */}
        {totals && totals.total_categories > 0 && (
          <div className="bg-[#0A0A0A] border border-white/10 p-4 md:p-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
              <h3 className="text-sm uppercase tracking-wider text-white/60">Resumen por Bodegas</h3>
              <div className="text-xs text-white/40">
                {totals.total_categories} categorías • {totals.total_products} productos
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
              {WAREHOUSES.map(wh => (
                <div key={wh.id} className={`p-3 md:p-4 border ${wh.bgFull}`}>
                  <p className="text-[10px] md:text-xs uppercase text-white/60 mb-1">{wh.name}</p>
                  <p className="text-lg md:text-xl font-bold">{totals[wh.id]?.units?.toLocaleString() || 0}</p>
                  <p className="text-xs md:text-sm text-green-400">{formatCurrency(totals[wh.id]?.value || 0)}</p>
                </div>
              ))}
              <div className="p-3 md:p-4 border border-white/20 bg-white/5">
                <p className="text-[10px] md:text-xs uppercase text-white/60 mb-1">Total</p>
                <p className="text-lg md:text-xl font-bold">{totals.total_units?.toLocaleString() || 0}</p>
                <p className="text-xs md:text-sm text-green-400">{formatCurrency(totals.total_value || 0)}</p>
              </div>
            </div>
          </div>
        )}

        {/* Lista de categorías */}
        <div className="space-y-3">
          {catList.length === 0 ? (
            <p className="text-white/40 text-center py-8">No hay categorías con productos</p>
          ) : (
            catList.map((cat) => (
              <div key={cat.slug} className="bg-[#0A0A0A] border border-white/10 overflow-hidden">
                {/* Header de categoría - clickeable */}
                <button
                  onClick={() => toggleCategoryDetail(cat.slug)}
                  className="w-full p-4 md:p-5 flex flex-col md:flex-row md:items-center justify-between gap-4 hover:bg-white/5 transition-colors text-left"
                >
                  <div className="flex items-center gap-3 md:gap-4">
                    <div className="w-10 h-10 md:w-12 md:h-12 bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0">
                      <Grid className="w-5 h-5 md:w-6 md:h-6 text-white/40" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-sm md:text-base">{cat.name}</h4>
                      <p className="text-[10px] md:text-xs text-white/40">{cat.product_count} productos</p>
                    </div>
                  </div>

                  {/* Datos por bodega */}
                  <div className="flex items-center gap-2 md:gap-4">
                    <div className="hidden md:flex gap-4">
                      {WAREHOUSES.map(wh => (
                        <div key={wh.id} className="text-center min-w-[60px]">
                          <p className="text-[10px] uppercase text-white/40">{wh.name}</p>
                          <p className={`text-sm font-semibold ${cat[wh.id]?.units > 0 ? (wh.id === 'china' ? 'text-red-400' : wh.id === 'contenedor' ? 'text-yellow-400' : 'text-green-400') : 'text-white/30'}`}>
                            {cat[wh.id]?.units || 0}
                          </p>
                        </div>
                      ))}
                    </div>
                    <div className="md:hidden flex gap-2">
                      <span className="text-red-400 text-xs">{cat.china?.units || 0}</span>
                      <span className="text-white/30">/</span>
                      <span className="text-yellow-400 text-xs">{cat.contenedor?.units || 0}</span>
                      <span className="text-white/30">/</span>
                      <span className="text-green-400 text-xs">{cat.stock?.units || 0}</span>
                    </div>
                    <div className="text-right border-l border-white/10 pl-3 md:pl-4 min-w-[80px] md:min-w-[100px]">
                      <p className="text-[10px] uppercase text-white/40">Total</p>
                      <p className="text-sm md:text-lg font-bold">{cat.total_units}</p>
                      <p className="text-xs text-green-400">{formatCurrency(cat.total_value)}</p>
                    </div>
                    <div className="ml-2">
                      {expandedCategory === cat.slug ? (
                        <ChevronUp className="w-5 h-5 text-white/40" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-white/40" />
                      )}
                    </div>
                  </div>
                </button>

                {/* Detalle expandible */}
                {expandedCategory === cat.slug && (
                  <div className="border-t border-white/10 bg-[#050505]">
                    {categoryDetailLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <RefreshCw className="w-5 h-5 animate-spin text-white/40" />
                      </div>
                    ) : categoryDetail ? (
                      <div className="p-4">
                        {/* Acciones de categoría */}
                        <div className="flex flex-wrap gap-2 mb-4">
                          <button
                            onClick={() => startCategoryTransfer(cat.slug)}
                            className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-[10px] md:text-xs uppercase tracking-wider transition-colors flex items-center gap-1"
                          >
                            <ArrowRightLeft className="w-3 h-3" />
                            Transferir Categoría
                          </button>
                          <button
                            onClick={() => {
                              setFilters({ ...filters, category: cat.slug });
                              setActiveSubTab('products');
                            }}
                            className="px-3 py-1.5 border border-white/20 hover:bg-white/10 text-white text-[10px] md:text-xs uppercase tracking-wider transition-colors flex items-center gap-1"
                          >
                            <Package className="w-3 h-3" />
                            Ver en Productos
                          </button>
                        </div>

                        {/* Tabla de productos */}
                        <div className="space-y-2">
                          {categoryDetail.products?.map((product) => (
                            <div key={product.id} className="flex flex-col sm:flex-row sm:items-center justify-between p-3 bg-[#0A0A0A] border border-white/5 gap-2">
                              <div className="flex items-center gap-3 flex-1 min-w-0">
                                {product.image && (
                                  <img src={product.image} alt={product.name} className="w-10 h-10 object-cover flex-shrink-0" />
                                )}
                                <div className="min-w-0">
                                  <p className="text-xs md:text-sm font-medium truncate">{product.name}</p>
                                  <p className="text-[10px] text-white/40">{product.brand} • {product.sku}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-3 sm:gap-4">
                                <div className="grid grid-cols-3 gap-2 text-center">
                                  <div>
                                    <p className="text-[9px] text-white/40">China</p>
                                    <p className={`text-xs ${product.inventory?.china > 0 ? 'text-red-400' : 'text-white/30'}`}>
                                      {product.inventory?.china || 0}
                                    </p>
                                  </div>
                                  <div>
                                    <p className="text-[9px] text-white/40">Cont.</p>
                                    <p className={`text-xs ${product.inventory?.contenedor > 0 ? 'text-yellow-400' : 'text-white/30'}`}>
                                      {product.inventory?.contenedor || 0}
                                    </p>
                                  </div>
                                  <div>
                                    <p className="text-[9px] text-white/40">Stock</p>
                                    <p className={`text-xs ${product.inventory?.stock > 0 ? 'text-green-400' : 'text-white/30'}`}>
                                      {product.inventory?.stock || 0}
                                    </p>
                                  </div>
                                </div>
                                <div className="text-right min-w-[70px]">
                                  <p className="text-sm font-semibold">{product.total_units}</p>
                                  <p className="text-[10px] text-green-400">{formatCurrency(product.total_value)}</p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    );
  };

  // ============================================
  // RENDER: DRAWER DE DETALLE DE BODEGA
  // ============================================
  const renderDetailDrawer = () => {
    if (!showDetailDrawer) return null;
    
    const warehouseInfo = drawerWarehouse === 'all' 
      ? { name: 'Total General', color: 'border-white/30' }
      : WAREHOUSES.find(w => w.id === drawerWarehouse) || { name: 'Desconocido', color: 'border-white/10' };

    // Calcular totales y obtener categorías únicas
    const drawerCategories = [...new Set(drawerProducts.map(p => p.category).filter(Boolean))].sort();
    
    // Filtrar productos por categoría
    const filteredDrawerProducts = drawerCategoryFilter === 'all' 
      ? drawerProducts 
      : drawerProducts.filter(p => p.category === drawerCategoryFilter);
    
    // Calcular totales de inversión
    const calculateTotals = (products) => {
      let totalUnits = 0;
      let totalValue = 0;
      
      products.forEach(product => {
        const inv = product.inventory || { china: 0, contenedor: 0, stock: 0 };
        const qty = drawerWarehouse === 'all' 
          ? (product.total_inventory || inv.china + inv.contenedor + inv.stock)
          : (product.quantity || inv[drawerWarehouse] || 0);
        const costPrice = product.cost_price || product.price || 0;
        totalUnits += qty;
        totalValue += product.value || (qty * costPrice);
      });
      
      return { totalUnits, totalValue, totalProducts: products.length };
    };
    
    const allTotals = calculateTotals(drawerProducts);
    const filteredTotals = drawerCategoryFilter === 'all' ? allTotals : calculateTotals(filteredDrawerProducts);

    return (
      <div className="fixed inset-0 z-50 flex justify-end" onClick={closeDrawer}>
        {/* Overlay */}
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
        
        {/* Drawer */}
        <div 
          className="relative w-full max-w-2xl bg-[#0A0A0A] border-l border-white/10 h-full overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className={`p-6 border-b ${warehouseInfo.color} bg-gradient-to-r from-[#111] to-[#0A0A0A]`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Warehouse className="w-6 h-6 text-white/60" />
                <div>
                  <h2 className="text-xl font-semibold">Detalle: {warehouseInfo.name}</h2>
                  <p className="text-xs text-white/50">{drawerProducts.length} productos</p>
                </div>
              </div>
              <button
                onClick={closeDrawer}
                className="p-2 hover:bg-white/10 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Resumen de Inversión Total */}
            <div className="mt-4 grid grid-cols-3 gap-3">
              <div className="bg-[#0A0A0A] border border-white/10 p-3 text-center">
                <p className="text-[10px] uppercase tracking-wider text-white/50">Productos</p>
                <p className="text-lg font-bold">{allTotals.totalProducts}</p>
              </div>
              <div className="bg-[#0A0A0A] border border-white/10 p-3 text-center">
                <p className="text-[10px] uppercase tracking-wider text-white/50">Unidades</p>
                <p className="text-lg font-bold">{allTotals.totalUnits.toLocaleString()}</p>
              </div>
              <div className="bg-[#0A0A0A] border border-green-500/20 p-3 text-center">
                <p className="text-[10px] uppercase tracking-wider text-white/50">Valor Invertido</p>
                <p className="text-lg font-bold text-green-400">{formatCurrency(allTotals.totalValue)}</p>
              </div>
            </div>
          </div>

          {/* Filtro por Categoría */}
          <div className="p-4 border-b border-white/10 bg-[#0D0D0D]">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2 flex-1 min-w-[200px]">
                <Filter className="w-4 h-4 text-white/40" />
                <select
                  value={drawerCategoryFilter}
                  onChange={(e) => setDrawerCategoryFilter(e.target.value)}
                  className="flex-1 bg-[#0A0A0A] border border-white/10 px-3 py-2 text-sm outline-none cursor-pointer"
                >
                  <option value="all">Todas las categorías ({drawerProducts.length})</option>
                  {drawerCategories.map(cat => {
                    const count = drawerProducts.filter(p => p.category === cat).length;
                    return (
                      <option key={cat} value={cat}>{cat} ({count})</option>
                    );
                  })}
                </select>
              </div>
              
              {drawerCategoryFilter !== 'all' && (
                <button
                  onClick={() => setDrawerCategoryFilter('all')}
                  className="px-3 py-2 text-xs text-white/60 hover:text-white border border-white/10 hover:border-white/30 transition-colors"
                >
                  Limpiar
                </button>
              )}
            </div>
            
            {/* Resumen filtrado */}
            {drawerCategoryFilter !== 'all' && (
              <div className="mt-3 p-3 bg-blue-500/10 border border-blue-500/30">
                <p className="text-xs text-blue-400 mb-2 uppercase tracking-wider font-semibold">
                  Filtro: {drawerCategoryFilter}
                </p>
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div>
                    <p className="text-[10px] text-white/50">Productos</p>
                    <p className="text-sm font-bold">{filteredTotals.totalProducts}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-white/50">Unidades</p>
                    <p className="text-sm font-bold">{filteredTotals.totalUnits.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-white/50">Valor Invertido</p>
                    <p className="text-sm font-bold text-green-400">{formatCurrency(filteredTotals.totalValue)}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {drawerLoading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-6 h-6 animate-spin text-white/40" />
              </div>
            ) : filteredDrawerProducts.length === 0 ? (
              <p className="text-white/40 text-center py-12">
                {drawerCategoryFilter === 'all' 
                  ? 'No hay productos en esta bodega' 
                  : `No hay productos de categoría "${drawerCategoryFilter}" en esta bodega`}
              </p>
            ) : (
              <div className="space-y-3">
                {/* Barra de selección masiva - Solo para bodegas específicas */}
                {drawerWarehouse && drawerWarehouse !== 'all' && (
                  <div className="flex flex-wrap items-center justify-between gap-2 p-3 bg-[#111] border border-white/10 mb-4">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => selectedDrawerProducts.size === filteredDrawerProducts.length ? clearDrawerSelection() : selectAllDrawerProducts()}
                        className="p-1.5 hover:bg-white/10 transition-colors"
                        title={selectedDrawerProducts.size === filteredDrawerProducts.length ? "Deseleccionar todos" : "Seleccionar todos"}
                      >
                        {selectedDrawerProducts.size === filteredDrawerProducts.length && filteredDrawerProducts.length > 0 ? (
                          <CheckSquare className="w-5 h-5 text-blue-400" />
                        ) : selectedDrawerProducts.size > 0 ? (
                          <CheckSquare className="w-5 h-5 text-white/40" />
                        ) : (
                          <Square className="w-5 h-5 text-white/40" />
                        )}
                      </button>
                      <span className="text-xs text-white/60">
                        {selectedDrawerProducts.size > 0 
                          ? `${selectedDrawerProducts.size} seleccionado(s)`
                          : 'Seleccionar para transferir'
                        }
                      </span>
                    </div>
                    
                    {selectedDrawerProducts.size > 0 && (
                      <>
                        <button
                          onClick={() => {
                            // Inicializar cantidades con el total disponible de cada producto
                            const initialQuantities = {};
                            selectedDrawerProducts.forEach(productId => {
                              const product = drawerProducts.find(p => p.id === productId);
                              if (product) {
                                const inv = product.inventory || { china: 0, contenedor: 0, stock: 0 };
                                const qty = product.quantity || inv[drawerWarehouse] || 0;
                                initialQuantities[productId] = qty;
                              }
                            });
                            setTransferQuantities(initialQuantities);
                            setShowDrawerTransferDialog(true);
                          }}
                          className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs uppercase tracking-wider transition-colors flex items-center gap-1"
                        >
                          <ArrowRightLeft className="w-3 h-3" />
                          Transferir ({selectedDrawerProducts.size})
                        </button>
                        {/* Botón Eliminar solo en Archivados */}
                        {drawerWarehouse === 'archivados' && (
                          <button
                            onClick={() => handleBulkDeleteFromArchived()}
                            className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-xs uppercase tracking-wider transition-colors flex items-center gap-1"
                          >
                            <Trash2 className="w-3 h-3" />
                            Eliminar ({selectedDrawerProducts.size})
                          </button>
                        )}
                      </>
                    )}
                  </div>
                )}

                {filteredDrawerProducts.map((product) => {
                  const inv = product.inventory || { china: 0, contenedor: 0, stock: 0 };
                  const qty = drawerWarehouse === 'all' 
                    ? (product.total_inventory || inv.china + inv.contenedor + inv.stock)
                    : (product.quantity || inv[drawerWarehouse] || 0);
                  const costPrice = product.cost_price || product.price || 0;
                  const value = product.value || (qty * costPrice);
                  const productImage = product.images?.[0] || null;
                  const isSelected = selectedDrawerProducts.has(product.id);
                  
                  return (
                    <div key={product.id} className={`p-4 bg-[#111] border transition-colors ${isSelected ? 'border-blue-500/50 bg-blue-500/5' : 'border-white/5 hover:border-white/10'}`}>
                      <div className="flex gap-4 items-start">
                        {/* Checkbox para selección - solo en bodegas específicas */}
                        {drawerWarehouse && drawerWarehouse !== 'all' && (
                          <button
                            onClick={() => toggleDrawerProductSelection(product.id)}
                            className="p-1 hover:bg-white/10 transition-colors flex-shrink-0 mt-2"
                          >
                            {isSelected ? (
                              <CheckSquare className="w-5 h-5 text-blue-400" />
                            ) : (
                              <Square className="w-5 h-5 text-white/30" />
                            )}
                          </button>
                        )}
                        
                        {/* Miniatura del producto */}
                        <div className="w-16 h-16 flex-shrink-0 bg-[#1a1a1a] border border-white/10 overflow-hidden">
                          {productImage ? (
                            <img 
                              src={productImage} 
                              alt={product.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Package className="w-6 h-6 text-white/20" />
                            </div>
                          )}
                        </div>
                        
                        {/* Info del producto */}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{product.name}</p>
                          <p className="text-xs text-white/40">{product.brand} • {product.sku}</p>
                          <p className="text-xs text-white/40">Categoría: {product.category}</p>
                        </div>
                        
                        {/* Cantidades y valor - Editable para bodegas específicas */}
                        <div className="text-right flex-shrink-0">
                          {drawerWarehouse && drawerWarehouse !== 'all' ? (
                            // Edición rápida para bodegas específicas (china, contenedor, stock)
                            quickEditInventory.productId === product.id && quickEditInventory.warehouse === drawerWarehouse ? (
                              <div className="flex items-center gap-2 justify-end">
                                <input
                                  type="number"
                                  min="0"
                                  value={quickEditInventory.value}
                                  onChange={(e) => setQuickEditInventory({ 
                                    ...quickEditInventory, 
                                    value: parseInt(e.target.value) || 0 
                                  })}
                                  className={`w-20 bg-[#1a1a1a] border px-2 py-1 text-sm text-center outline-none ${
                                    drawerWarehouse === 'china' ? 'border-red-500/50 text-red-400 focus:border-red-400' :
                                    drawerWarehouse === 'contenedor' ? 'border-yellow-500/50 text-yellow-400 focus:border-yellow-400' :
                                    'border-green-500/50 text-green-400 focus:border-green-400'
                                  }`}
                                  autoFocus
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleQuickInventorySave(product.id, drawerWarehouse);
                                    if (e.key === 'Escape') cancelQuickInventoryEdit();
                                  }}
                                />
                                <button
                                  onClick={() => handleQuickInventorySave(product.id, drawerWarehouse)}
                                  disabled={savingInventory}
                                  className={`p-1 rounded-sm transition-all ${
                                    drawerWarehouse === 'china' ? 'bg-red-500/20 border border-red-500/50 hover:bg-red-500/30' :
                                    drawerWarehouse === 'contenedor' ? 'bg-yellow-500/20 border border-yellow-500/50 hover:bg-yellow-500/30' :
                                    'bg-green-500/20 border border-green-500/50 hover:bg-green-500/30'
                                  }`}
                                  title="Guardar"
                                >
                                  <Check className={`w-4 h-4 ${
                                    drawerWarehouse === 'china' ? 'text-red-400' :
                                    drawerWarehouse === 'contenedor' ? 'text-yellow-400' :
                                    'text-green-400'
                                  }`} />
                                </button>
                                <button
                                  onClick={cancelQuickInventoryEdit}
                                  className="p-1 bg-white/10 border border-white/20 hover:bg-white/20 rounded-sm transition-all"
                                  title="Cancelar"
                                >
                                  <X className="w-4 h-4 text-white/60" />
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => setQuickEditInventory({ 
                                  productId: product.id, 
                                  warehouse: drawerWarehouse, 
                                  value: qty 
                                })}
                                className={`text-lg font-bold cursor-pointer transition-all hover:underline ${
                                  drawerWarehouse === 'china' ? 'text-red-400 hover:text-red-300' :
                                  drawerWarehouse === 'contenedor' ? 'text-yellow-400 hover:text-yellow-300' :
                                  'text-green-400 hover:text-green-300'
                                }`}
                                title="Clic para editar cantidad"
                              >
                                {qty} <span className="text-xs text-white/40">uds</span>
                              </button>
                            )
                          ) : (
                            // Vista normal para "todos"
                            <p className="text-lg font-bold">{qty} <span className="text-xs text-white/40">uds</span></p>
                          )}
                          <p className="text-sm text-green-400">{formatCurrency(value)}</p>
                        </div>
                      </div>
                      {drawerWarehouse === 'all' && (
                        <div className="mt-3 pt-3 border-t border-white/5 flex gap-4 ml-20">
                          <span className="text-xs text-red-400">China: {inv.china || 0}</span>
                          <span className="text-xs text-yellow-400">Contenedor: {inv.contenedor || 0}</span>
                          <span className="text-xs text-green-400">Stock: {inv.stock || 0}</span>
                        </div>
                      )}
                      
                      {/* Botones de acción: Editar y Archivar - Especialmente para Sin Stock */}
                      {drawerWarehouse && (drawerWarehouse === 'sin_stock' || drawerWarehouse === 'archivados' || drawerWarehouse === 'all') && (
                        <div className="mt-3 pt-3 border-t border-white/5 flex gap-2 justify-end">
                          <button
                            onClick={() => handleEditProduct(product)}
                            className="px-3 py-1.5 bg-white/10 border border-white/20 hover:bg-white/20 text-xs flex items-center gap-1.5 transition-colors"
                            title="Editar producto"
                          >
                            <Edit className="w-3.5 h-3.5" />
                            Editar
                          </button>
                          {drawerWarehouse !== 'archivados' && (
                            <button
                              onClick={() => handleArchiveProduct(product.id)}
                              className="px-3 py-1.5 bg-orange-500/10 border border-orange-500/30 hover:bg-orange-500/20 text-orange-400 text-xs flex items-center gap-1.5 transition-colors"
                              title="Archivar producto"
                            >
                              <ArchiveX className="w-3.5 h-3.5" />
                              Archivar
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer con acción rápida */}
          {drawerWarehouse && drawerWarehouse !== 'all' && drawerProducts.length > 0 && (
            <div className="p-4 border-t border-white/10 bg-[#0A0A0A]">
              <button
                onClick={() => {
                  setTransferMode('all');
                  setTransferData({
                    ...transferData,
                    from_warehouse: drawerWarehouse,
                    to_warehouse: drawerWarehouse === 'china' ? 'contenedor' : 'stock'
                  });
                  closeDrawer();
                  setShowTransferDialog(true);
                }}
                className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white text-xs uppercase tracking-wider font-semibold transition-colors flex items-center justify-center gap-2"
              >
                <MoveRight className="w-4 h-4" />
                Transferir todo desde {warehouseInfo.name}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div>
      {/* Header con acciones */}
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
        <h2 className="text-2xl font-semibold">Inventario por Bodegas</h2>
        <div className="flex gap-3">
          <button
            onClick={() => {
              setTransferMode('product');
              setShowTransferDialog(true);
            }}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 text-xs uppercase tracking-wider transition-colors"
            data-testid="new-transfer-button"
          >
            <ArrowRightLeft className="w-4 h-4" />
            Nueva Transferencia
          </button>
          <button
            onClick={fetchData}
            className="p-2 border border-white/20 hover:bg-white/10 transition-colors"
            title="Actualizar"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-8 border-b border-white/10 overflow-x-auto">
        {[
          { id: 'summary', label: 'Resumen', icon: Package },
          { id: 'stats', label: 'Estadísticas', icon: BarChart3 },
          { id: 'categories', label: 'Categorías', icon: Grid },
          { id: 'products', label: 'Productos', icon: Package },
          { id: 'movements', label: 'Movimientos', icon: ArrowUpCircle },
          { id: 'transfers', label: 'Transferencias', icon: ArrowRightLeft }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveSubTab(tab.id)}
            data-testid={`inv-tab-${tab.id}`}
            className={`flex items-center gap-2 px-3 md:px-4 py-3 text-[10px] md:text-xs uppercase tracking-wider transition-all whitespace-nowrap ${
              activeSubTab === tab.id 
                ? 'text-white border-b-2 border-white' 
                : 'text-white/60 hover:text-white'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            <span className="hidden sm:inline">{tab.label}</span>
            <span className="sm:hidden">{tab.label.slice(0, 4)}</span>
          </button>
        ))}
      </div>

      {/* Contenido */}
      {activeSubTab === 'stats' ? (
        <InventoryStats />
      ) : loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-6 h-6 animate-spin text-white/40" />
        </div>
      ) : (
        <>
          {activeSubTab === 'summary' && renderSummary()}
          {activeSubTab === 'categories' && renderCategories()}
          {activeSubTab === 'products' && renderProducts()}
          {activeSubTab === 'movements' && renderMovements()}
          {activeSubTab === 'transfers' && renderTransfers()}
        </>
      )}

      {/* Drawer de detalle */}
      {renderDetailDrawer()}

      {/* Dialog: Edición de producto desde drawer */}
      {showEditDialog && editingProduct && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[60] flex items-center justify-center p-4" onClick={() => { setShowEditDialog(false); setEditingProduct(null); }}>
          <div className="bg-[#0A0A0A] border border-white/10 p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold">Editar Producto</h3>
              <button 
                onClick={() => { setShowEditDialog(false); setEditingProduct(null); }}
                className="p-1 hover:bg-white/10 rounded transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Info del producto */}
            <div className="flex gap-4 mb-6 p-4 bg-[#111] border border-white/5">
              {editingProduct.images?.[0] && (
                <img 
                  src={editingProduct.images[0]} 
                  alt={editingProduct.name}
                  className="w-20 h-20 object-cover flex-shrink-0"
                />
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white/40">SKU: {editingProduct.sku}</p>
                <p className="text-xs text-white/40 mt-1">{editingProduct.brand} • {editingProduct.category}</p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-xs uppercase tracking-wider text-white/60 mb-2">Nombre</label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="w-full bg-[#121212] border border-white/10 focus:border-white/30 px-4 py-3 text-sm outline-none"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs uppercase tracking-wider text-white/60 mb-2">Precio Venta</label>
                  <input
                    type="number"
                    min="0"
                    value={editForm.price}
                    onChange={(e) => setEditForm({ ...editForm, price: parseFloat(e.target.value) || 0 })}
                    className="w-full bg-[#121212] border border-white/10 focus:border-white/30 px-4 py-3 text-sm outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider text-white/60 mb-2">Stock (Bodega Stock)</label>
                  <input
                    type="number"
                    min="0"
                    value={editForm.stock}
                    onChange={(e) => setEditForm({ ...editForm, stock: parseInt(e.target.value) || 0 })}
                    className="w-full bg-[#121212] border border-green-500/30 focus:border-green-500/50 px-4 py-3 text-sm outline-none text-green-400"
                  />
                </div>
              </div>
              
              <p className="text-xs text-white/40">
                Al actualizar el stock aquí, se sincronizará automáticamente con la bodega "Stock".
              </p>
            </div>
            
            <div className="flex gap-4 mt-6">
              <button
                onClick={handleSaveEditProduct}
                className="flex-1 bg-white text-black hover:bg-gray-200 py-3 text-xs uppercase tracking-wider font-semibold transition-colors"
              >
                Guardar Cambios
              </button>
              <button
                onClick={() => { setShowEditDialog(false); setEditingProduct(null); }}
                className="flex-1 border border-white/20 py-3 text-xs uppercase tracking-wider hover:bg-white/5 transition-colors"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dialog: Transferencia masiva desde drawer */}
      {showDrawerTransferDialog && drawerWarehouse && drawerWarehouse !== 'all' && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[60] flex items-center justify-center p-4" onClick={() => setShowDrawerTransferDialog(false)}>
          <div className="bg-[#0A0A0A] border border-white/10 p-6 max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-2">Transferir Productos</h3>
            <p className="text-sm text-white/60 mb-4">
              Desde <span className="font-semibold text-white">{WAREHOUSES.find(w => w.id === drawerWarehouse)?.name}</span>
            </p>
            
            {/* Lista de productos con cantidades editables */}
            <div className="flex-1 overflow-y-auto mb-4 max-h-[40vh]">
              <div className="space-y-2">
                {Array.from(selectedDrawerProducts).map(productId => {
                  const product = drawerProducts.find(p => p.id === productId);
                  if (!product) return null;
                  
                  const inv = product.inventory || { china: 0, contenedor: 0, stock: 0 };
                  const maxQty = product.quantity || inv[drawerWarehouse] || 0;
                  const currentQty = transferQuantities[productId] ?? maxQty;
                  
                  return (
                    <div key={productId} className="flex items-center gap-3 p-3 bg-[#111] border border-white/5">
                      {/* Imagen miniatura */}
                      {product.images?.[0] ? (
                        <img src={product.images[0]} alt="" className="w-10 h-10 object-cover flex-shrink-0" />
                      ) : (
                        <div className="w-10 h-10 bg-white/5 flex items-center justify-center flex-shrink-0">
                          <Package className="w-4 h-4 text-white/20" />
                        </div>
                      )}
                      
                      {/* Info del producto */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{product.name}</p>
                        <p className="text-xs text-white/40">Disponible: {maxQty} uds</p>
                      </div>
                      
                      {/* Input de cantidad */}
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setTransferQuantities(prev => ({
                            ...prev,
                            [productId]: Math.max(1, (prev[productId] ?? maxQty) - 1)
                          }))}
                          className="w-7 h-7 flex items-center justify-center bg-white/10 hover:bg-white/20 transition-colors"
                        >
                          -
                        </button>
                        <input
                          type="number"
                          min="1"
                          max={maxQty}
                          value={currentQty}
                          onChange={(e) => {
                            const val = parseInt(e.target.value) || 1;
                            setTransferQuantities(prev => ({
                              ...prev,
                              [productId]: Math.min(Math.max(1, val), maxQty)
                            }));
                          }}
                          className="w-14 h-7 bg-[#0A0A0A] border border-white/20 text-center text-sm outline-none focus:border-blue-500"
                        />
                        <button
                          onClick={() => setTransferQuantities(prev => ({
                            ...prev,
                            [productId]: Math.min(maxQty, (prev[productId] ?? maxQty) + 1)
                          }))}
                          className="w-7 h-7 flex items-center justify-center bg-white/10 hover:bg-white/20 transition-colors"
                        >
                          +
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            
            {/* Resumen de transferencia */}
            <div className="mb-4 p-3 bg-blue-500/10 border border-blue-500/20">
              <p className="text-xs text-blue-400">
                Total a transferir: <span className="font-bold">
                  {Object.values(transferQuantities).reduce((sum, qty) => sum + (qty || 0), 0)} unidades
                </span> de {selectedDrawerProducts.size} producto(s)
              </p>
            </div>
            
            {/* Bodega Destino */}
            <div className="mb-4">
              <label className="block text-xs uppercase tracking-wider text-white/60 mb-2">Bodega Destino</label>
              <div className="grid grid-cols-2 gap-2">
                {WAREHOUSES.filter(w => w.id !== drawerWarehouse).map(wh => (
                  <button
                    key={wh.id}
                    onClick={() => setDrawerTransferTarget(wh.id)}
                    className={`p-2 border text-center transition-all text-sm ${
                      drawerTransferTarget === wh.id 
                        ? `${wh.color} border-2` 
                        : 'border-white/10 hover:border-white/30'
                    }`}
                  >
                    {wh.name}
                  </button>
                ))}
              </div>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDrawerTransferDialog(false);
                  setTransferQuantities({});
                }}
                className="flex-1 px-4 py-2 border border-white/20 text-white/80 hover:bg-white/10 text-sm transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleDrawerBulkTransfer}
                disabled={transferExecuting}
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {transferExecuting ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <ArrowRightLeft className="w-4 h-4" />
                )}
                Transferir
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dialog: Movimiento de Inventario */}
      {showMovementDialog && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={closeMovementDialog}>
          <div className="bg-[#0A0A0A] border border-white/10 p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-6">
              {movementType === 'entrada' ? 'Registrar Entrada' : 'Registrar Salida'}
            </h3>
            
            {selectedProduct && (
              <p className="text-sm text-white/60 mb-4">Producto: <span className="text-white">{selectedProduct.name}</span></p>
            )}
            
            <div className="space-y-4">
              <div>
                <label className="block text-xs uppercase tracking-wider mb-2 text-white/60">Bodega</label>
                <select
                  value={movementData.warehouse}
                  onChange={(e) => setMovementData({...movementData, warehouse: e.target.value})}
                  className="w-full bg-[#121212] border border-white/10 px-4 py-3 text-sm outline-none cursor-pointer"
                >
                  {WAREHOUSES.map(wh => (
                    <option key={wh.id} value={wh.id}>{wh.name}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-xs uppercase tracking-wider mb-2 text-white/60">Cantidad</label>
                <input
                  type="number"
                  min="1"
                  value={movementData.quantity}
                  onChange={(e) => setMovementData({...movementData, quantity: e.target.value})}
                  className="w-full bg-[#121212] border border-white/10 px-4 py-3 text-sm outline-none"
                />
              </div>
              
              <div>
                <label className="block text-xs uppercase tracking-wider mb-2 text-white/60">Notas (opcional)</label>
                <textarea
                  value={movementData.notes}
                  onChange={(e) => setMovementData({...movementData, notes: e.target.value})}
                  className="w-full bg-[#121212] border border-white/10 px-4 py-3 text-sm outline-none h-20 resize-none"
                />
              </div>
            </div>
            
            <div className="flex gap-4 mt-6">
              <button
                onClick={handleCreateMovement}
                className="flex-1 bg-white text-black py-3 text-xs uppercase tracking-wider font-semibold hover:bg-gray-200 transition-colors"
              >
                Registrar
              </button>
              <button
                onClick={closeMovementDialog}
                className="flex-1 border border-white/20 py-3 text-xs uppercase tracking-wider hover:bg-white/5 transition-colors"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dialog: Transferencia entre Bodegas (MEJORADO) */}
      {showTransferDialog && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={closeTransferDialog}>
          <div className="bg-[#0A0A0A] border border-white/10 p-6 max-w-xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-2">Transferencia entre Bodegas</h3>
            <p className="text-xs text-white/40 mb-4">Flujo recomendado: China → Contenedor → Stock</p>
            
            {/* Tabs de modo de transferencia */}
            <div className="flex gap-1 mb-6 border-b border-white/10">
              {[
                { id: 'product', label: 'Por Producto', icon: Package },
                { id: 'category', label: 'Por Categoría', icon: Layers },
                { id: 'all', label: 'Trasladar Todo', icon: MoveRight }
              ].map(mode => (
                <button
                  key={mode.id}
                  onClick={() => {
                    setTransferMode(mode.id);
                    setTransferPreview(null);
                  }}
                  className={`flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-wider transition-all ${
                    transferMode === mode.id 
                      ? 'text-white border-b-2 border-blue-500' 
                      : 'text-white/60 hover:text-white'
                  }`}
                >
                  <mode.icon className="w-3 h-3" />
                  {mode.label}
                </button>
              ))}
            </div>
            
            <div className="space-y-4">
              {/* MODO: Por Producto */}
              {transferMode === 'product' && (
                <div>
                  <label className="block text-xs uppercase tracking-wider mb-2 text-white/60">Producto</label>
                  <select
                    value={transferData.product_id}
                    onChange={(e) => setTransferData({...transferData, product_id: e.target.value})}
                    className="w-full bg-[#121212] border border-white/10 px-4 py-3 text-sm outline-none cursor-pointer"
                  >
                    <option value="">Seleccionar producto...</option>
                    {products.map(p => (
                      <option key={p.id} value={p.id}>{p.name} ({p.sku})</option>
                    ))}
                  </select>
                </div>
              )}

              {/* MODO: Por Categoría */}
              {transferMode === 'category' && (
                <div>
                  <label className="block text-xs uppercase tracking-wider mb-2 text-white/60">Categoría</label>
                  <select
                    value={transferData.category}
                    onChange={(e) => setTransferData({...transferData, category: e.target.value})}
                    className="w-full bg-[#121212] border border-white/10 px-4 py-3 text-sm outline-none cursor-pointer"
                  >
                    <option value="">Seleccionar categoría...</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.slug}>{cat.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* MODO: Trasladar Todo */}
              {transferMode === 'all' && (
                <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 mb-4">
                  <p className="text-sm text-yellow-400 font-semibold mb-1">Transferencia masiva</p>
                  <p className="text-xs text-white/60">
                    Esta acción transferirá TODOS los productos que tengan stock en la bodega de origen.
                  </p>
                </div>
              )}
              
              {/* Selectores de bodega */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs uppercase tracking-wider mb-2 text-white/60">Desde</label>
                  <select
                    value={transferData.from_warehouse}
                    onChange={(e) => setTransferData({...transferData, from_warehouse: e.target.value})}
                    className="w-full bg-[#121212] border border-white/10 px-4 py-3 text-sm outline-none cursor-pointer"
                  >
                    {WAREHOUSES.map(wh => (
                      <option key={wh.id} value={wh.id}>{wh.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider mb-2 text-white/60">Hacia</label>
                  <select
                    value={transferData.to_warehouse}
                    onChange={(e) => setTransferData({...transferData, to_warehouse: e.target.value})}
                    className="w-full bg-[#121212] border border-white/10 px-4 py-3 text-sm outline-none cursor-pointer"
                  >
                    {WAREHOUSES.map(wh => (
                      <option key={wh.id} value={wh.id}>{wh.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Cantidad (solo para producto individual) */}
              {transferMode === 'product' && (
                <div>
                  <label className="block text-xs uppercase tracking-wider mb-2 text-white/60">Cantidad</label>
                  <input
                    type="number"
                    min="1"
                    value={transferData.quantity}
                    onChange={(e) => setTransferData({...transferData, quantity: parseInt(e.target.value) || 1})}
                    className="w-full bg-[#121212] border border-white/10 px-4 py-3 text-sm outline-none"
                  />
                </div>
              )}
              
              {/* Notas */}
              <div>
                <label className="block text-xs uppercase tracking-wider mb-2 text-white/60">Observaciones (opcional)</label>
                <textarea
                  value={transferData.notes}
                  onChange={(e) => setTransferData({...transferData, notes: e.target.value})}
                  placeholder="Ej: Llegada de contenedor #123"
                  className="w-full bg-[#121212] border border-white/10 px-4 py-3 text-sm outline-none h-16 resize-none"
                />
              </div>

              {/* Preview de transferencia masiva */}
              {(transferMode === 'category' || transferMode === 'all') && (
                <div className="border border-white/10 bg-[#111] p-4">
                  <h4 className="text-xs uppercase tracking-wider text-white/60 mb-3">Vista previa</h4>
                  
                  {previewLoading ? (
                    <div className="flex items-center justify-center py-4">
                      <RefreshCw className="w-5 h-5 animate-spin text-white/40" />
                    </div>
                  ) : transferPreview ? (
                    <div className="space-y-3">
                      <div className="grid grid-cols-3 gap-4 text-center">
                        <div>
                          <p className="text-2xl font-bold">{transferPreview.total_products}</p>
                          <p className="text-xs text-white/40">Productos</p>
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-blue-400">{transferPreview.total_units}</p>
                          <p className="text-xs text-white/40">Unidades</p>
                        </div>
                        <div>
                          <p className="text-xl font-bold text-green-400">{formatCurrency(transferPreview.total_value)}</p>
                          <p className="text-xs text-white/40">Valor</p>
                        </div>
                      </div>
                      
                      {transferPreview.products && transferPreview.products.length > 0 && (
                        <div className="max-h-40 overflow-y-auto mt-3 border-t border-white/5 pt-3">
                          {transferPreview.products.slice(0, 5).map((p, idx) => (
                            <div key={idx} className="flex justify-between text-xs py-1">
                              <span className="text-white/60 truncate flex-1 mr-2">{p.name}</span>
                              <span className="text-white">{p.quantity} uds</span>
                            </div>
                          ))}
                          {transferPreview.products.length > 5 && (
                            <p className="text-xs text-white/40 mt-2">... y {transferPreview.products.length - 5} más</p>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-xs text-white/40 text-center py-4">
                      {transferMode === 'category' && !transferData.category 
                        ? 'Selecciona una categoría para ver la vista previa'
                        : transferData.from_warehouse === transferData.to_warehouse
                        ? 'Selecciona bodegas diferentes'
                        : 'No hay productos para transferir'}
                    </p>
                  )}
                </div>
              )}
            </div>
            
            {/* Botones de acción */}
            <div className="flex gap-4 mt-6">
              {transferMode === 'product' ? (
                <button
                  onClick={handleCreateTransfer}
                  disabled={transferExecuting || !transferData.product_id}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-600/50 text-white py-3 text-xs uppercase tracking-wider font-semibold transition-colors"
                >
                  {transferExecuting ? 'Transferiendo...' : 'Transferir'}
                </button>
              ) : (
                <button
                  onClick={() => setShowConfirmBulk(true)}
                  disabled={transferExecuting || !transferPreview || transferPreview.total_products === 0}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-600/50 text-white py-3 text-xs uppercase tracking-wider font-semibold transition-colors"
                >
                  {transferExecuting ? 'Transferiendo...' : `Transferir ${transferPreview?.total_products || 0} productos`}
                </button>
              )}
              <button
                onClick={closeTransferDialog}
                className="flex-1 border border-white/20 py-3 text-xs uppercase tracking-wider hover:bg-white/5 transition-colors"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dialog: Confirmación de transferencia masiva */}
      {showConfirmBulk && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-[60] flex items-center justify-center p-4" onClick={() => setShowConfirmBulk(false)}>
          <div className="bg-[#0A0A0A] border-2 border-yellow-500/50 p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle className="w-6 h-6 text-yellow-400" />
              <h3 className="text-lg font-semibold text-yellow-400">Confirmar Transferencia Masiva</h3>
            </div>
            
            <div className="space-y-4 mb-6">
              <p className="text-sm text-white/80">
                Estás a punto de transferir:
              </p>
              
              <div className="bg-[#111] p-4 border border-white/10">
                <div className="flex justify-between mb-2">
                  <span className="text-white/60">Productos:</span>
                  <span className="font-bold">{transferPreview?.total_products || 0}</span>
                </div>
                <div className="flex justify-between mb-2">
                  <span className="text-white/60">Unidades totales:</span>
                  <span className="font-bold text-blue-400">{transferPreview?.total_units || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/60">Valor total:</span>
                  <span className="font-bold text-green-400">{formatCurrency(transferPreview?.total_value)}</span>
                </div>
              </div>
              
              <div className="flex items-center justify-center gap-3 text-sm">
                <span className={`px-3 py-1 ${WAREHOUSES.find(w => w.id === transferData.from_warehouse)?.color}`}>
                  {WAREHOUSES.find(w => w.id === transferData.from_warehouse)?.name}
                </span>
                <MoveRight className="w-5 h-5 text-white/60" />
                <span className={`px-3 py-1 ${WAREHOUSES.find(w => w.id === transferData.to_warehouse)?.color}`}>
                  {WAREHOUSES.find(w => w.id === transferData.to_warehouse)?.name}
                </span>
              </div>
              
              <p className="text-xs text-yellow-400/80 text-center">
                Esta acción no se puede deshacer fácilmente.
              </p>
            </div>
            
            <div className="flex gap-4">
              <button
                onClick={handleBulkTransfer}
                disabled={transferExecuting}
                className="flex-1 bg-yellow-600 hover:bg-yellow-700 disabled:bg-yellow-600/50 text-black py-3 text-xs uppercase tracking-wider font-bold transition-colors flex items-center justify-center gap-2"
              >
                {transferExecuting ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Procesando...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4" />
                    Confirmar Transferencia
                  </>
                )}
              </button>
              <button
                onClick={() => setShowConfirmBulk(false)}
                disabled={transferExecuting}
                className="flex-1 border border-white/20 py-3 text-xs uppercase tracking-wider hover:bg-white/5 transition-colors"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InventoryPanel;
